import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Breadcrumbs,
  Grid,
  List,
  ListItem,
  IconButton,
} from '@mui/material';
import SouthIcon from '@mui/icons-material/South';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import gsap from 'gsap';
import { useParams } from 'react-router-dom';
import SliderWrapper from '../../../Components/SliderWrapper';
import type ReactSlick from 'react-slick';
import { PortfolioDetailsNormalized } from './PortfolioData';
import type { PortfolioDetail } from './PortfolioData';

// ----- helpers (unchanged behavior) -----
type Img = { src: string; alt: string };

const readableLabel = (p: PortfolioDetail) =>
  p.title?.trim() || p.heading?.trim() || p.breadcrumb?.current?.trim() || p.id;

const toImg = (img?: string | Img, fb = ''): Img | null => {
  if (!img) return null;
  return typeof img === 'string'
    ? { src: img, alt: fb }
    : { src: img.src, alt: (img.alt || fb).trim() };
};

const normalizeId = (id: string) => id.toLowerCase().replace(/\s+/g, '-');

const AllPortfolioDetails: React.FC = () => {
  const { id } = useParams(); // /portfolio/:id
  const sliderRef = useRef<ReactSlick>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Hooks first (unchanged)
  useEffect(() => {
    if (gridRef.current?.children) {
      gsap.fromTo(
        gridRef.current.children,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 1, stagger: 0.2, ease: 'power4.out' },
      );
    }
  }, []);

  useEffect(() => {
    contentRefs.current.forEach((ref, index) => {
      if (ref) {
        const isActive = activeIndex === index;
        const height = ref.scrollHeight;
        gsap.to(ref, {
          height: isActive ? height : 0,
          duration: 0.4,
          ease: 'power2.inOut',
          onComplete: () => {
            if (isActive && ref) ref.style.height = 'auto';
          },
        });
      }
    });
  }, [activeIndex]);

  const handleScroll = (targetId: string) => {
    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      gsap.to(window, {
        scrollTo: { y: targetElement, offsetY: 50 },
        duration: 1,
        ease: 'power2.inOut',
      });
    }
  };

  const handleImageClick = (portfolioId: string) => {
    window.location.href = `/portfolio/${portfolioId}`;
  };

  const settings = {
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    centerMode: true,
    centerPadding: '0',
    responsive: [{ breakpoint: 900, settings: { slidesToShow: 1 } }],
  };

  // find item AFTER hooks are declared (use normalized data)
  const portfolio = PortfolioDetailsNormalized.find(
    (item) => normalizeId(item.id) === normalizeId(id || ''),
  );

  if (!portfolio) {
    return (
      <Container sx={{ mt: 20 }}>
        <Typography variant="h4" color="error">
          Portfolio not found.
        </Typography>
      </Container>
    );
  }

  // normalize images safely
  const label = readableLabel(portfolio);
  const main = toImg(portfolio.mainImage as any, `${label} – main image`)!;
  const top = toImg(portfolio.topImage as any, `${label} – top image`);
  const bottom = toImg(portfolio.bottomImage as any, `${label} – bottom image`);

  const heroSrc = top?.src ?? main.src;
  const heroAlt = (top?.alt || `${label} – top image`).trim();

  return (
    <Box sx={{ color: '#000', pt: 24 }}>
      {/* Header band styled like PortfolioSection */}
      <Box sx={{ bgcolor: '#0f63a5', pt: 6, pb: 6 }}>
        <Container maxWidth="lg" sx={{ borderTop: '1px solid #777777', pt: 2 }}>
          {/* Breadcrumbs */}
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Breadcrumbs separator={<span style={{ color: '#fff' }}>–</span>}>
              <Box component="span" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {portfolio.breadcrumb.previous}
              </Box>
              <Typography sx={{ color: '#fff' }}>
                {portfolio.breadcrumb.current}
              </Typography>
            </Breadcrumbs>
          </Box>

          {/* Title */}
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: 42, md: 72 },
              fontWeight: 700,
              color: '#fff', // match PortfolioSection white-on-blue
            }}
          >
            {portfolio.heading}
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ mt: 6 }}>
        {/* Scroll Button over hero (unchanged behavior) */}
        <Box sx={{ position: 'relative', mb: 6, textAlign: 'start' }}>
          <Box
            component="a"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              handleScroll('#text');
            }}
            sx={{
              display: 'flex',
              width: 120,
              height: 120,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#171818',
              border: '10px solid #171818',
              borderRadius: '50%',
              position: 'absolute',
              top: -60,
              left: '50%',
              transform: 'translateX(-50%)',
              transition: 'all .3s ease',
              zIndex: 2,
              '&:hover': { backgroundColor: '#0f63a5' },
            }}
          >
            <SouthIcon sx={{ width: 50, height: 50, color: '#fff' }} />
          </Box>

          {/* <Box
            sx={{
              width: '100%',
              maxWidth: '720px',
              height: '500px',
              borderRadius: '40px',
              overflow: 'hidden',
              mx: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src={portfolio.topImage}
              alt="Best Real Estate Mentor in Hyderabad"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '40px',
                display: 'block',
              }}
            />
          </Box>
          {portfolio.bottomImage && (
            <Box
              sx={{
                mb: 6,
                width: '100%',
                height: '500px', // set your desired height here
                borderRadius: '40px',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ImageReveal
                src={portfolio.bottomImage}
                alt="Best Real Estate Mentor in Hyderabad"
                width="100%"
                height="100%"
                threshold={0.8}
                scaleDuration={3}
                sx={{ borderRadius: '40px', width: '100%', height: '100%' }}
              />
            </Box>
          )} */}
          <Grid
            container
            spacing={2}
            sx={{
              mb: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Grid
              item
              xs={12}
              md={bottom ? 6 : 12}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '720px',
                  height: '500px',
                  borderRadius: '40px',
                  overflow: 'hidden',
                  mx: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={heroSrc}
                  alt={heroAlt}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '40px',
                    display: 'block',
                  }}
                />
              </Box>
            </Grid>

            {bottom && (
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '720px',
                    height: '500px',
                    borderRadius: '40px',
                    overflow: 'hidden',
                    mx: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src={bottom.src}
                    alt={(bottom.alt || `${label} – bottom image`).trim()}
                    loading="lazy"
                    decoding="async"
                    sx={{
                      borderRadius: '40px',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        <Container maxWidth="md">
          {/* <Box sx={{ backgroundColor: "#0f63a5", padding: 4, borderRadius: '10px', mb: 5 }}>
            <Grid container ref={gridRef} spacing={2}>
              
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="h6" color="white">
                  Program
                </Typography>
                <Typography variant="body2" color="white" mt={1}>
                  {portfolio.projectData.program}
                </Typography>
              </Grid>

              
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="h6" color="white">
                  Year:
                </Typography>
                <Typography variant="body2" color="white" mt={1}>
                  {portfolio.projectData.year}
                </Typography>
              </Grid>

              
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="h6" color="white">
                  Location:
                </Typography>
                <Typography variant="body2" color="white" mt={1}>
                  {portfolio.projectData.location}
                </Typography>
              </Grid>


            </Grid>
          </Box> */}
        </Container>
      </Container>

      {/* Text Blocks (already blue like PortfolioSection) */}
      <Box sx={{ background: '#0f63a5', py: 4 }}>
        <Container maxWidth="lg">
          <Box id="text" sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{ textAlign: 'start', color: 'white' }}
            >
              {portfolio.title}
            </Typography>
          </Box>

          {/* Paragraphs */}
          <Box sx={{ mb: 4 }}>
            {Array.isArray(portfolio.paragraph) &&
              portfolio.paragraph.map((text: string, index: number) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{ textAlign: 'start', mb: 2, color: 'white' }}
                >
                  {text}
                </Typography>
              ))}
          </Box>

          {/* Bullet List */}
          {portfolio.bulletList && portfolio.bulletList.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <List sx={{ listStyle: 'none', pl: 0 }}>
                {portfolio.bulletList.map((text, i) => (
                  <ListItem
                    key={i}
                    sx={{
                      position: 'relative',
                      pl: 3,
                      mb: i === 2 ? 6 : 2,
                      color: 'white',
                      '&::before': {
                        content: '""',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: '#0f63a5',
                        position: 'absolute',
                        left: 0,
                        top: 12,
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ textAlign: 'start' }}>
                      {text}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Section Title and Text */}
          {(portfolio.sectionTitle || portfolio.sectionText) && (
            <Box sx={{ mb: 6 }}>
              {portfolio.sectionTitle && (
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: 36,
                    mb: 2,
                    textAlign: 'start',
                    color: 'white',
                  }}
                >
                  {portfolio.sectionTitle}
                </Typography>
              )}
              {portfolio.sectionText && (
                <Typography
                  variant="body2"
                  sx={{ textAlign: 'start', color: 'white' }}
                >
                  {portfolio.sectionText}
                </Typography>
              )}
            </Box>
          )}

          {/* Steps */}
          {Array.isArray(portfolio.steps) && portfolio.steps.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <Grid container spacing={6}>
                {portfolio.steps.map((_, i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <Box sx={{ pl: { xs: 0, md: 6 }, textAlign: 'start' }}>
                      <Typography
                        component="span"
                        sx={{
                          display: 'inline-block',
                          fontSize: 18,
                          fontWeight: 700,
                          mb: 1,
                          color: 'white',
                        }}
                      >
                        {String(i + 1).padStart(2, '0')}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Container>
      </Box>

      {/* Bottom Image */}
      {/* <Box sx={{ background: '#0f63a5', py: 6 }}>
        <Container maxWidth="lg">

          <Box id="text" sx={{ mb: 4 }}>
            {Array.isArray(portfolio.finalText) && portfolio.finalText.map((text: string, index: number) => (
              <Typography key={index} variant="body2" sx={{ textAlign: 'start', mb: 2, color: 'white' }}>
                {text}
              </Typography>
            ))}
          </Box>
        </Container>
      </Box> */}
      <Box sx={{ backgroundColor: '', py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ mx: 'auto', px: 2 }}>
            {/* <Slider ref={sliderRef} {...settings}>


              {PortfolioDetailsData.map((portfolio, index) => (
                <Box
                  key={index}
                  sx={{
                    px: 2, // Adds space between images
                  }}
                  onClick={() => handleImageClick(portfolio.id)} // Navigate on click
                >
                  <Box
                    sx={{
                      width: '300px',
                      height: '220px',
                      borderRadius: '20px',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                    }}
                  >
                    <Box
                    component='img'
                      src={portfolio.mainImage}
                      alt={`slide-${index}`}
                      // width="100%"
                      // height="100%"
                      // threshold={0.8}
                      // scaleDuration={3}
                      sx={{
                        borderRadius: '20px',
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Slider> */}

            <SliderWrapper ref={sliderRef} {...settings}>
              {PortfolioDetailsNormalized.map((p, index) => {
                const thumb = toImg(
                  p.mainImage as any,
                  `${readableLabel(p)} – slide`,
                )!;
                return (
                  <Grid container spacing={0} key={index}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          px: 2,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                        onClick={() => handleImageClick(normalizeId(p.id))}
                      >
                        <Box
                          sx={{
                            width: '300px',
                            height: '220px',
                            borderRadius: '20px',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                          }}
                        >
                          <Box
                            component="img"
                            src={thumb.src}
                            alt={thumb.alt}
                            loading="lazy"
                            decoding="async"
                            sx={{
                              borderRadius: '20px',
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                );
              })}
            </SliderWrapper>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 4,
                py: 2,
                alignItems: 'center',
                background: '#0f63a5',
                borderRadius: '10px',
              }}
            >
              <IconButton
                onClick={() => sliderRef.current?.slickPrev()}
                sx={{ color: '#fff', px: 2, py: 1 }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16, mr: 1 }} />
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: '#fff' }}
                >
                  Previous
                </Typography>
              </IconButton>

              <IconButton
                onClick={() => sliderRef.current?.slickNext()}
                sx={{ color: '#fff', px: 2, py: 1 }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ mr: 1, color: '#fff' }}
                >
                  Next
                </Typography>
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AllPortfolioDetails;
